import React, { useState, useEffect, useImperativeHandle, forwardRef, useRef } from "react";
import DataGrid from "../../common/dataGrid/dataGrid";
import {
  convertToSearchField,
  generateColumnMetadata,
  orderObj,
  mask,
  Feature,
} from "../../../utils/utils";
import DataGridHeader from "../../common/dataGrid/dataGridAddPatientHeader";
import { commonPostApi } from "../../../utils/apiService";
import { API_ENDPOINTS } from "../../../utils/apiEndpoints";
import {
  nonRequirePatientListField,PatientListTableColumns
} from "../../../utils/serviceData";
import { Button } from "primereact/button";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { exportCSV, exportExcel, exportPdf } from "../../../utils/exportUtils";
import { SORT_ORDER } from "../../../constants/sortOrder";

const PatientList = forwardRef(({ 
  onAddPatientClick,
  onEditPatient,
  onAddVisit,
  onDeletePatient 
}, ref) => {
  const navigate = useNavigate();
  const [columns, setColumns] = useState([]);
  const [values, setValues] = useState([]);
  const [loading, setLoading] = useState(true);
  const searchFields = ["patient_name", "patient_code", "phone_no","dob","gender"];
  const id = useSelector((state) => state.common.camp?.camp_details_id);
  const [navigator, setNavigator] = useState({
    page: 1,
    first: 0,
    rows: 10,
    totalRecords: 1,
    sortBy: "patient_name",
    sortDir: SORT_ORDER.ASC,
    searchCriteria: convertToSearchField(searchFields, ""),
  });
  const navigatorRef = useRef(navigator);
  
  useEffect(() => {
    navigatorRef.current = navigator;
  }, [navigator]);

  const init = async (navigator) => {
    let currentObj = {
      query: {
        page: navigator?.page,
        limit: navigator?.rows,
        sortFields: [[navigator?.sortBy, navigator?.sortDir]],
        searchCriteria: navigator?.searchCriteria,
      },
    };

    await commonPostApi(API_ENDPOINTS.PATIENT_DETAILS, currentObj)
      .then((data = []) => {
        if (data?.data?.data) {
          const finalData = data?.data?.data;
          const updatedPatient = finalData.map((p) => ({
            ...p,
            aadhar_no: mask(p.aadhar_no),
          }));
          setNavigator((prev) => ({
            ...prev,
            totalRecords: data?.data?.total,
            page: data?.data?.currentPage,
          }));
          setColumns(
            orderObj(
              [
                "patient_name",
                "patient_code",
                "aadhar_no",
                "gender",
                "dob",
                "phone_no",
              ],
              generateColumnMetadata({
                data: finalData,
                nonRequireField: nonRequirePatientListField,
                alias: {
                  patient_name: "patient",
                  patient_code: "patient_id",
                  aadhar_no: "aadhaar_no",
                },
                defaultColumnsArr:PatientListTableColumns,
              })
            )
          );
          setValues(updatedPatient);
          setLoading(false);
        } else {
          setNavigator((prev) => ({
            ...prev,
            totalRecords: 1,
            page: 1,
          }));
        }
      })
      .catch((err) => {
        console.log(err);
      });
  };

  const fetchAllPatientsForExport = async () => {
    const exportObj = {
      query: {
        page: 1,
        limit: navigator.totalRecords, // fetch all records
        sortFields: [[navigator.sortBy, navigator.sortDir]],
        searchCriteria: navigator.searchCriteria,
      },
    };

    const res = await commonPostApi(API_ENDPOINTS.PATIENT_DETAILS, exportObj);
    const data = res?.data?.data || [];
    return data.map((p) => ({
      ...p,
      aadhar_no: mask(p.aadhar_no),
    }));
  };

  const itemRenderer = (item, props) => (
    <div className="p-menuitem-content">
      {item.label === "Add visit" &&
        Feature?.featureVerify(Feature.listOfFeature.patientVisitAdd) && (
          <Button
            className="card-button-add"
            icon="pi pi-plus"
            label="Add Visit"
            onClick={() =>
              onAddVisit(props?.props?.itemProp?.patient_id)
            }
          />
        )}
      {item.label === "Edit" &&
        Feature?.featureVerify(Feature.listOfFeature.patientEdit) && (
          <Button
            className="card-button-edit-secound"
            label="Edit"
            onClick={() => {
              onEditPatient(props?.props?.itemProp?.patient_id);
            }}
          />
        )}
      {item.label === "Remove" &&
        Feature?.featureVerify(Feature.listOfFeature.patientEdit) && (
          <Button
            className="card-button-delete"
            label="Delete"
            onClick={() => {
              onDeletePatient(props?.props?.itemProp?.patient_id);
            }}
          />
        )}
    </div>
  );

  useEffect(() => {
    init(navigatorRef.current);
  }, [id]);

  function onPageChange(event) {
    setLoading(true);
    setNavigator((prev) => ({
      ...prev,
      first: event?.first,
      page: event.page + 1,
    }));
    init({
      ...navigator,
      first: event?.first,
      page: event.page + 1,
    });
  }

  const onRowCountChange = (e) => {
    setLoading(true);
    setNavigator((prev) => ({
      ...prev,
      rows: e?.target?.value,
      page: 1,
      first: 0,
    }));
    init({
      ...navigator,
      page: 1,
      first: 0,
      rows: e?.target?.value,
    });
  };

  const onSortClick = (event) => {
    setLoading(true);
    const sortOrder = event?.sortOrder === 1 ? SORT_ORDER.ASC : SORT_ORDER.DESC;
    setNavigator((prev) => ({
      ...prev,
      sortBy: event?.sortField,
      sortDir: sortOrder,
      sortFields: [[event?.sortField, sortOrder]],
    }));
    init({
      ...navigator,
      sortBy: event?.sortField,
      sortDir: sortOrder,
      sortFields: [[event?.sortField, sortOrder]],
    });
  };

  const onRowClick = (event) => {
    if (Feature?.featureVerify(Feature.listOfFeature.patientVisitView)) {
      navigate(`/patient/${event?.data?.patient_id}`);
    }
  };

  function handleSeachChange(value) {
    setNavigator((prev) => ({
      ...prev,
      searchCriteria: convertToSearchField(searchFields, value),
    }));
    init({
      ...navigator,
      searchCriteria: convertToSearchField(searchFields, value),
    });
  }

  useImperativeHandle(ref, () => ({
    refresh: () => {
      setLoading(true);
      init(navigatorRef.current);
    },
  }));

  return (
    <DataGrid
      header={
        <DataGridHeader
          handleClick={onAddPatientClick}
          isSearchEnabled={true}
          isDownloadButton={true}
          isCalenderEnabled={false}
          isButtonEnabledLeft={false}
          exportCSV={async () => {
            const allData = await fetchAllPatientsForExport();
            exportCSV(allData, columns, "Patient_List");
          }}
          exportExcel={async () => {
            const allData = await fetchAllPatientsForExport();
            exportExcel(allData, columns, "Patient_List");
          }}
          exportPdf={async () => {
            const allData = await fetchAllPatientsForExport();
            exportPdf(allData, columns, "Patient_List");
          }}
          isButtonEnabledRight={Feature?.featureVerify(
            Feature.listOfFeature.patientAdd
          )}
          searchPlaceHolder={"Search Patients"}
          currentPageHeader={"Patient List"}
          onChange={handleSeachChange}
        />
      }
      onSortClick={onSortClick}
      onRowCountChange={onRowCountChange}
      onRowClick={onRowClick}
      onPageChange={onPageChange}
      sortable={true}
      navigator={navigator}
      column={columns}
      value={values}
      loading={loading}
      optionsView={
        (Feature?.featureVerify(Feature.listOfFeature.patientVisitAdd) ||
          Feature?.featureVerify(Feature.listOfFeature.patientEdit)) && [
          {
            items: [
              {
                label: "Add visit",
                icon: "pi pi-pencil",
                template: itemRenderer,
              },
              {
                label: "Edit",
                icon: "pi pi-upload",
                template: itemRenderer,
              },
              {
                label: "Remove",
                icon: "pi pi-upload",
                template: itemRenderer,
              },
            ],
          },
        ]
      }
      isAccordionEnabled={false}
      isPaginatorEnabled={true}
    />
  );
});

export default PatientList;